import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import {
  formatCuit,
  formatMoneda,
  listarRetencionesEditables,
  obtenerGruposPreview,
  sumarImportesRetenciones,
} from "./utils";

const META_TIPO = {
  ganancias: {
    label: "Ganancias",
    color: "#1a5276",
    bg: "rgba(26, 82, 118, 0.08)",
    border: "rgba(26, 82, 118, 0.28)",
  },
  iva: {
    label: "IVA",
    color: "#1e8449",
    bg: "rgba(30, 132, 73, 0.08)",
    border: "rgba(30, 132, 73, 0.28)",
  },
  iibb: {
    label: "IIBB",
    color: "#b9770e",
    bg: "rgba(185, 119, 14, 0.1)",
    border: "rgba(185, 119, 14, 0.3)",
  },
};

const metaDe = (tipo) => META_TIPO[tipo] || META_TIPO.iibb;

const ImporteEditable = ({ value, onCommit, disabled, autoFocus = false }) => {
  const [borrador, setBorrador] = useState(
    value != null && value !== "" ? String(value) : ""
  );
  const [focused, setFocused] = useState(false);

  React.useEffect(() => {
    setBorrador(value != null && value !== "" ? String(value) : "");
  }, [value]);

  const confirmar = () => {
    setFocused(false);
    const raw = String(borrador ?? "").trim().replace(",", ".");
    if (raw === "") {
      onCommit(0);
      return;
    }
    const n = parseFloat(raw);
    if (!Number.isFinite(n) || n < 0) {
      setBorrador(value != null && value !== "" ? String(value) : "");
      return;
    }
    const redondeado = Math.round(n * 100) / 100;
    setBorrador(String(redondeado));
    if (Math.abs(redondeado - (Number(value) || 0)) >= 0.005) {
      onCommit(redondeado);
    }
  };

  return (
    <TextField
      size="small"
      type="number"
      value={borrador}
      disabled={disabled}
      autoFocus={autoFocus}
      onChange={(e) => setBorrador(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={confirmar}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.target.blur();
        }
        if (e.key === "Escape") {
          setBorrador(value != null && value !== "" ? String(value) : "");
          e.target.blur();
        }
      }}
      onWheel={(e) => e.target.blur()}
      inputProps={{ min: 0, step: 0.01 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Typography
              component="span"
              sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}
            >
              $
            </Typography>
          </InputAdornment>
        ),
      }}
      sx={{
        width: { xs: "100%", sm: 148 },
        "& .MuiOutlinedInput-root": {
          height: 36,
          fontSize: 14,
          fontWeight: 600,
          bgcolor: focused ? "#fff" : "rgba(255,255,255,0.7)",
          transition: "box-shadow 0.15s ease, border-color 0.15s ease",
          ...(focused
            ? {
                boxShadow: "0 0 0 3px rgba(36, 113, 163, 0.15)",
              }
            : {}),
        },
        "& .MuiInputBase-input": { py: 0.75, px: 0.5, textAlign: "right" },
      }}
    />
  );
};

const FilaRetencion = ({
  item,
  disabled,
  onCommitImporte,
  onPedirEliminar,
}) => {
  const [editando, setEditando] = useState(false);
  const meta = metaDe(item.tipo);
  const detalle =
    item.base != null
      ? `Base ${formatMoneda(item.base)}${
          item.alicuota != null ? ` · ${item.alicuota}%` : ""
        }`
      : null;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1.25,
        px: 1.5,
        py: 1.25,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: meta.border,
        bgcolor: meta.bg,
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          borderColor: meta.color,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        },
      }}
    >
      <Chip
        size="small"
        label={meta.label}
        sx={{
          height: 22,
          fontWeight: 700,
          fontSize: 11,
          color: meta.color,
          bgcolor: "#fff",
          border: "1px solid",
          borderColor: meta.border,
          flexShrink: 0,
        }}
      />

      <Box sx={{ flex: "1 1 160px", minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight={700}
          noWrap
          title={item.label}
          sx={{ lineHeight: 1.3 }}
        >
          {item.label}
        </Typography>
        {detalle ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", lineHeight: 1.3 }}
          >
            {detalle}
          </Typography>
        ) : null}
      </Box>

      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ ml: { xs: 0, sm: "auto" }, width: { xs: "100%", sm: "auto" } }}
      >
        {editando ? (
          <ImporteEditable
            value={item.importe}
            disabled={disabled}
            autoFocus
            onCommit={(importe) => {
              onCommitImporte(importe);
              setEditando(false);
            }}
          />
        ) : (
          <Tooltip title="Editar importe">
            <Button
              size="small"
              variant="outlined"
              disabled={disabled}
              onClick={() => setEditando(true)}
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{
                minWidth: 120,
                height: 36,
                px: 1.25,
                justifyContent: "space-between",
                borderColor: meta.border,
                color: "text.primary",
                bgcolor: "#fff",
                fontWeight: 700,
                fontSize: 13,
                textTransform: "none",
                "&:hover": {
                  borderColor: meta.color,
                  bgcolor: "#fff",
                },
              }}
            >
              {formatMoneda(item.importe)}
            </Button>
          </Tooltip>
        )}

        <Tooltip title="Eliminar retención">
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={disabled}
              onClick={onPedirEliminar}
              aria-label={`Eliminar ${item.label}`}
              sx={{
                bgcolor: "#fff",
                border: "1px solid",
                borderColor: "rgba(211, 47, 47, 0.25)",
                borderRadius: 1,
                width: 36,
                height: 36,
                "&:hover": {
                  bgcolor: "rgba(211, 47, 47, 0.06)",
                  borderColor: "error.main",
                },
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
};

const BloqueProveedor = ({
  grupo,
  disabled,
  onEditarRetencion,
  onPedirEliminar,
  defaultOpen = true,
}) => {
  const [abierto, setAbierto] = useState(defaultOpen);
  const items = listarRetencionesEditables(grupo.retenciones);
  const totalGrupo = sumarImportesRetenciones(grupo.retenciones);
  const nombre =
    grupo.razon_social || grupo.proveedor?.razon_social || "Proveedor";

  if (!items.length) return null;

  return (
    <Box
      sx={{
        border: "1px solid #e6e9ec",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setAbierto((v) => !v)}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.5,
          py: 1.1,
          m: 0,
          border: 0,
          cursor: "pointer",
          textAlign: "left",
          bgcolor: "#f7f9fb",
          borderBottom: abierto ? "1px solid #e6e9ec" : "none",
          "&:hover": { bgcolor: "#f0f4f7" },
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={700} noWrap>
            {nombre}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatCuit(grupo.cuit)} · {items.length} retención
            {items.length === 1 ? "" : "es"}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={formatMoneda(totalGrupo)}
          sx={{
            fontWeight: 700,
            bgcolor: "rgba(36, 113, 163, 0.1)",
            color: "#1a5276",
            height: 26,
          }}
        />
        {abierto ? (
          <ExpandLessIcon fontSize="small" color="action" />
        ) : (
          <ExpandMoreIcon fontSize="small" color="action" />
        )}
      </Box>

      <Collapse in={abierto}>
        <Stack spacing={1} sx={{ p: 1.25 }}>
          {items.map((item) => (
            <FilaRetencion
              key={item.clave}
              item={item}
              disabled={disabled}
              onCommitImporte={(importe) =>
                onEditarRetencion?.(grupo.cuit, item.clave, { importe })
              }
              onPedirEliminar={() =>
                onPedirEliminar({
                  cuit: grupo.cuit,
                  clave: item.clave,
                  label: item.label,
                  nombre,
                  importe: item.importe,
                })
              }
            />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};

/**
 * Editor de retenciones por proveedor (paso Medio de pago).
 * Editar/eliminar una retención solo afecta a ese proveedor.
 */
const EditorRetencionesMedio = ({
  preview,
  onEditarRetencion,
  disabled = false,
}) => {
  const grupos = useMemo(() => obtenerGruposPreview(preview), [preview]);
  const [pendienteEliminar, setPendienteEliminar] = useState(null);

  const gruposConItems = useMemo(
    () =>
      grupos.filter(
        (g) => listarRetencionesEditables(g.retenciones).length > 0
      ),
    [grupos]
  );

  const totalGeneral = useMemo(
    () =>
      gruposConItems.reduce(
        (acc, g) => acc + sumarImportesRetenciones(g.retenciones),
        0
      ),
    [gruposConItems]
  );

  const cantidadItems = useMemo(
    () =>
      gruposConItems.reduce(
        (acc, g) => acc + listarRetencionesEditables(g.retenciones).length,
        0
      ),
    [gruposConItems]
  );

  if (!grupos.length) return null;

  const confirmarEliminar = () => {
    if (!pendienteEliminar) return;
    onEditarRetencion?.(pendienteEliminar.cuit, pendienteEliminar.clave, {
      eliminar: true,
    });
    setPendienteEliminar(null);
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
            bgcolor: "#fff",
            borderBottom: "1px solid #eceff1",
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(36, 113, 163, 0.1)",
              color: "#2471A3",
              flexShrink: 0,
            }}
          >
            <ReceiptLongIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
              Retenciones
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", lineHeight: 1.35 }}
            >
              Tocá el importe para editarlo o eliminá una retención sin afectar
              al resto de proveedores.
            </Typography>
          </Box>
          {cantidadItems > 0 ? (
            <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {cantidadItems} ítem{cantidadItems === 1 ? "" : "s"} ·{" "}
                {gruposConItems.length} proveedor
                {gruposConItems.length === 1 ? "" : "es"}
              </Typography>
              <Typography
                variant="subtitle1"
                fontWeight={800}
                color="primary.main"
                lineHeight={1.2}
              >
                {formatMoneda(totalGeneral)}
              </Typography>
            </Box>
          ) : null}
        </Box>

        <Box sx={{ p: 1.5, bgcolor: "#fafbfc" }}>
          {gruposConItems.length === 0 ? (
            <Box
              sx={{
                py: 3,
                px: 2,
                textAlign: "center",
                border: "1px dashed #d0d7de",
                borderRadius: 1.5,
                bgcolor: "#fff",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No hay retenciones calculadas para los proveedores
                seleccionados.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.25}>
              {gruposConItems.map((grupo, idx) => (
                <BloqueProveedor
                  key={String(grupo.cuit)}
                  grupo={grupo}
                  disabled={disabled}
                  onEditarRetencion={onEditarRetencion}
                  onPedirEliminar={setPendienteEliminar}
                  defaultOpen={gruposConItems.length <= 3 || idx === 0}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Paper>

      <Dialog
        open={Boolean(pendienteEliminar)}
        onClose={() => setPendienteEliminar(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Eliminar retención</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {pendienteEliminar ? (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Se va a quitar{" "}
                  <strong>{pendienteEliminar.label}</strong> de{" "}
                  <strong>{pendienteEliminar.nombre}</strong>.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Importe: {formatMoneda(pendienteEliminar.importe)}. El total a
                  pagar de ese proveedor se recalcula al instante.
                </Typography>
              </>
            ) : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPendienteEliminar(null)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmarEliminar}
            disabled={disabled}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditorRetencionesMedio;
